import {
  getLeoUuid,
  LeoUUID,
  getList,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyQuestionViewOrder, SurveyQuestionViewOrderDTO } from "./surveyQuestionViewOrder";
import { SurveyViewResults, SurveyViewResultsDTO, SurveyViewResultsEnums } from "./surveyViewResults";
/* eslint-disable import/export */
export abstract class ManageDashboardViewRPC implements
  LeoRPC<ManageDashboardViewRPC.Request, ManageDashboardViewRPC.Response, ManageDashboardViewRPC.Errors.Errors>
{
  abstract execute(
    request: ManageDashboardViewRPC.Request
  ): Promise<LeoRPCResult<ManageDashboardViewRPC.Response, ManageDashboardViewRPC.Errors.Errors>>;
}

export namespace ManageDashboardViewRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    surveyViewId: string;
    surveyQuestionViewOrders: SurveyQuestionViewOrderDTO[];
  }
  
  export class Request implements LeoRPCRequest {
    readonly isManageDashboardViewRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly surveyViewId: LeoUUID;
    readonly surveyQuestionViewOrders: SurveyQuestionViewOrder[];
  
    static readonly constraints = {
      "surveyQuestionViewOrders": {
        "minSize": 1,
      }
    }
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    surveyViewId: LeoUUID, 
    surveyQuestionViewOrders: SurveyQuestionViewOrder[]
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.surveyViewId = surveyViewId;
      Request.validateSurveyQuestionViewOrders(surveyQuestionViewOrders);
      this.surveyQuestionViewOrders = surveyQuestionViewOrders;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyViewId = getLeoUuid(dto, "surveyViewId", InvalidRequestSurveyViewIdError);
      const surveyQuestionViewOrders = getList<SurveyQuestionViewOrderDTO, SurveyQuestionViewOrder>(
        dto,
        "surveyQuestionViewOrders",
        SurveyQuestionViewOrder.fromDTO,
        InvalidRequestSurveyQuestionViewOrdersError
      );
      return new Request(
        projectId,
        surveyId,
        surveyViewId,
        surveyQuestionViewOrders
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          surveyViewId: this.surveyViewId.toDTO(),
          surveyQuestionViewOrders: this.surveyQuestionViewOrders.map((e: SurveyQuestionViewOrder) => { return e.toDTO() })
      };
      return dto;
    }
    private static validateSurveyQuestionViewOrders = (surveyQuestionViewOrders: SurveyQuestionViewOrder[]): void => {
      if(surveyQuestionViewOrders.length < 1) {
        throw new InvalidRequestSurveyQuestionViewOrdersMinSizeError(
          1,
          `Attribute surveyQuestionViewOrders is too short. Size is ${surveyQuestionViewOrders.length}. Min size is 1.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `ManageDashboardViewRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',surveyViewId='${this.surveyViewId}',surveyQuestionViewOrders='${this.surveyQuestionViewOrders}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyViewResults: SurveyViewResultsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isManageDashboardViewResponse: boolean = true;
  
    readonly surveyViewResults: SurveyViewResults;
  
  
    constructor(
    surveyViewResults: SurveyViewResults
    ) {
      this.surveyViewResults = surveyViewResults;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyViewResults = SurveyViewResults.fromDTO(getObject(dto, "surveyViewResults", InvalidResponseSurveyViewResultsError));
      return new Response(
        surveyViewResults
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyViewResults: this.surveyViewResults.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `ManageDashboardView(surveyViewResults='${this.surveyViewResults}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidSurveyViewId = "INVALID_SURVEY_VIEW_ID",
    InvalidQuestionViewOrders = "INVALID_QUESTION_VIEW_ORDERS",
    OutdatedQuestionViewsFound = "OUTDATED_QUESTION_VIEWS_FOUND",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    MasterSurveyViewCannotBeEdited = "MASTER_SURVEY_VIEW_CANNOT_BE_EDITED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ManageDashboardViewRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `ManageDashboardViewRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidSurveyViewIdDTO {
      code: string;
    }
    
    export class InvalidSurveyViewId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyViewId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyViewIdDTO {
        const dto: InvalidSurveyViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyViewId.prototype.toString = function toString(): string {
      return `ManageDashboardViewRPC.INVALID_SURVEY_VIEW_ID()`;
    };
    
    
    export interface InvalidQuestionViewOrdersDTO {
      code: string;
    }
    
    export class InvalidQuestionViewOrders extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionViewOrders: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionViewOrders;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionViewOrdersDTO {
        const dto: InvalidQuestionViewOrdersDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionViewOrders.prototype.toString = function toString(): string {
      return `ManageDashboardViewRPC.INVALID_QUESTION_VIEW_ORDERS()`;
    };
    
    
    export interface OutdatedQuestionViewsFoundDTO {
      code: string;
    }
    
    export class OutdatedQuestionViewsFound extends LeoRPCError<RPCError> {
      readonly isOutdatedQuestionViewsFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.OutdatedQuestionViewsFound;
        super(code);
      }
    
    
      toDTO(): OutdatedQuestionViewsFoundDTO {
        const dto: OutdatedQuestionViewsFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    OutdatedQuestionViewsFound.prototype.toString = function toString(): string {
      return `ManageDashboardViewRPC.OUTDATED_QUESTION_VIEWS_FOUND()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ManageDashboardViewRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface MasterSurveyViewCannotBeEditedDTO {
      code: string;
    }
    
    export class MasterSurveyViewCannotBeEdited extends LeoRPCError<RPCError> {
      readonly isMasterSurveyViewCannotBeEdited: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MasterSurveyViewCannotBeEdited;
        super(code);
      }
    
    
      toDTO(): MasterSurveyViewCannotBeEditedDTO {
        const dto: MasterSurveyViewCannotBeEditedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MasterSurveyViewCannotBeEdited.prototype.toString = function toString(): string {
      return `ManageDashboardViewRPC.MASTER_SURVEY_VIEW_CANNOT_BE_EDITED()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | InvalidSurveyViewId | InvalidQuestionViewOrders | OutdatedQuestionViewsFound | ProjectAlreadyArchived | MasterSurveyViewCannotBeEdited;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | InvalidSurveyViewIdDTO | InvalidQuestionViewOrdersDTO | OutdatedQuestionViewsFoundDTO | ProjectAlreadyArchivedDTO | MasterSurveyViewCannotBeEditedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidSurveyViewId:
          return new InvalidSurveyViewId();
        case RPCError.InvalidQuestionViewOrders:
          return new InvalidQuestionViewOrders();
        case RPCError.OutdatedQuestionViewsFound:
          return new OutdatedQuestionViewsFound();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.MasterSurveyViewCannotBeEdited:
          return new MasterSurveyViewCannotBeEdited();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends ManageDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends ManageDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyViewIdError extends ManageDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewIdError";
  }
}

export class InvalidRequestSurveyQuestionViewOrdersError extends ManageDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyQuestionViewOrdersError";
  }
}

export class InvalidRequestSurveyQuestionViewOrdersMinSizeError extends InvalidRequestSurveyQuestionViewOrdersError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyQuestionViewOrdersMinSizeError";
  }
}

export class InvalidResponseSurveyViewResultsError extends ManageDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyViewResultsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends ManageDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends ManageDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidSurveyViewIdError extends ManageDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyViewIdError";
  }
}

export class InvalidResponseInvalidQuestionViewOrdersError extends ManageDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionViewOrdersError";
  }
}

export class InvalidResponseOutdatedQuestionViewsFoundError extends ManageDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseOutdatedQuestionViewsFoundError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ManageDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseMasterSurveyViewCannotBeEditedError extends ManageDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMasterSurveyViewCannotBeEditedError";
  }
}

